import React from 'react';
import styled from 'styled-components';
import { useLanguage } from '../../contexts/LanguageContext';

const Container = styled.div`
  max-width: 720px;
  margin: 0 auto;
  padding: 24px 16px 64px;
  @media (min-width: 640px) { padding: 40px 24px 80px; }
`;

const Title = styled.h1`
  font-size: 1.3rem;
  font-weight: 700;
  color: ${(p) => p.theme.colors.text};
  margin: 0 0 32px 0;
`;

const Section = styled.section`
  margin-bottom: 28px;
`;

const SectionTitle = styled.h2`
  font-size: 1rem;
  font-weight: 700;
  color: ${(p) => p.theme.colors.text};
  margin: 0 0 12px 0;
`;

const Body = styled.div`
  font-size: 0.85rem;
  color: ${(p) => p.theme.colors.textSecondary};
  line-height: 1.8;

  p { margin: 0 0 10px 0; }
  ul { margin: 0 0 10px 0; padding-left: 20px; }
  li { margin-bottom: 4px; }
`;

const LastUpdated = styled.p`
  font-size: 0.75rem;
  color: ${(p) => p.theme.colors.textMuted};
  margin-top: 32px;
`;

function TermsPage() {
  const { t } = useLanguage();

  return (
    <Container>
      <Title>{t('利用規約', 'Terms of Service')}</Title>

      <Section>
        <SectionTitle>{t('第1条（適用）', 'Article 1 (Application)')}</SectionTitle>
        <Body>
          <p>
            {t(
              '本規約は、STRUCT CODE（以下「本サービス」）の利用に関する条件を定めるものです。ユーザーは本規約に同意の上、本サービスをご利用ください。',
              'These Terms govern the use of STRUCT CODE (the "Service"). By using the Service, you agree to these Terms.'
            )}
          </p>
        </Body>
      </Section>

      <Section>
        <SectionTitle>{t('第2条（サービス内容）', 'Article 2 (Service Description)')}</SectionTitle>
        <Body>
          <p>
            {t(
              '本サービスは、性格診断・レポート生成・AIチャットなどの機能を提供するWebサービスです。サービス内容は予告なく変更される場合があります。',
              'The Service provides personality diagnosis, report generation, AI chat, and related features as a web service. Service features may change without notice.'
            )}
          </p>
        </Body>
      </Section>

      <Section>
        <SectionTitle>{t('第3条（料金・支払い）', 'Article 3 (Pricing & Payment)')}</SectionTitle>
        <Body>
          <p>
            {t(
              '有料プランの料金は、サービス内およびアカウント設定ページに表示される金額（税込）に従います。支払いはStripeを通じたクレジットカード決済で行われます。',
              'Paid plan pricing follows the amounts (tax included) displayed in the Service and Account Settings page. Payments are processed via Stripe credit card billing.'
            )}
          </p>
          <p>
            {t(
              'サブスクリプションは自動更新されます。解約はStripeポータルまたはアカウント設定からいつでも行えます。解約後も契約期間終了まで引き続きご利用いただけます。',
              'Subscriptions auto-renew. Cancellation is available anytime via the Stripe portal or Account Settings. Access continues until the end of the billing period.'
            )}
          </p>
        </Body>
      </Section>

      <Section>
        <SectionTitle>{t('第4条（禁止事項）', 'Article 4 (Prohibited Actions)')}</SectionTitle>
        <Body>
          <ul>
            <li>{t('法令または公序良俗に反する行為', 'Actions that violate laws or public order')}</li>
            <li>{t('本サービスの運営を妨害する行為', 'Actions that interfere with Service operation')}</li>
            <li>{t('他のユーザーの情報を不正に収集する行為', 'Unauthorized collection of other users\' information')}</li>
            <li>{t('リバースエンジニアリング、スクレイピング等の行為', 'Reverse engineering, scraping, or similar actions')}</li>
            <li>{t('第三者になりすます行為', 'Impersonation of third parties')}</li>
            <li>{t('アカウントの売買・譲渡', 'Selling or transferring accounts')}</li>
          </ul>
        </Body>
      </Section>

      <Section>
        <SectionTitle>{t('第5条（知的財産権）', 'Article 5 (Intellectual Property)')}</SectionTitle>
        <Body>
          <p>
            {t(
              '本サービスに含まれるコンテンツ（テキスト、画像、ソフトウェア等）に関する知的財産権は、当社または正当な権利者に帰属します。',
              'Intellectual property rights in Service content (text, images, software, etc.) belong to us or their rightful owners.'
            )}
          </p>
        </Body>
      </Section>

      <Section>
        <SectionTitle>{t('第6条（個人情報の取り扱い）', 'Article 6 (Handling of Personal Information)')}</SectionTitle>
        <Body>
          <p>
            {t(
              'ユーザーの個人情報、診断データ、およびAIチャットの会話内容の取り扱いについては、別途定める「プライバシーポリシー」によるものとします。ユーザーは、本サービスの利用をもって、プライバシーポリシーの内容に同意したものとみなされます。',
              'The handling of users\' personal information, diagnosis data, and AI chat conversation content is governed by our separate Privacy Policy. By using the Service, users are deemed to have agreed to the Privacy Policy.'
            )}
          </p>
        </Body>
      </Section>

      <Section>
        <SectionTitle>{t('第7条（免責事項）', 'Article 7 (Disclaimer)')}</SectionTitle>
        <Body>
          <p>
            {t(
              '本サービスの診断結果やAIによる回答は参考情報であり、医学的・心理学的な診断や助言を構成するものではありません。本サービスの利用により生じた損害について、当社は法令で許容される範囲で責任を負いません。',
              'Diagnosis results and AI responses are for reference only and do not constitute medical or psychological advice. We are not liable for damages arising from use of the Service, to the extent permitted by law.'
            )}
          </p>
        </Body>
      </Section>

      <Section>
        <SectionTitle>{t('第8条（サービスの変更・終了）', 'Article 8 (Service Changes & Termination)')}</SectionTitle>
        <Body>
          <p>
            {t(
              '当社は、事前の通知なくサービス内容の変更または終了を行うことがあります。サービス終了の場合、有料契約中のユーザーには合理的な期間内に通知いたします。',
              'We may change or terminate the Service without prior notice. For paid subscribers, we will provide reasonable notice before termination.'
            )}
          </p>
        </Body>
      </Section>

      <Section>
        <SectionTitle>{t('第9条（準拠法・管轄）', 'Article 9 (Governing Law)')}</SectionTitle>
        <Body>
          <p>
            {t(
              '本規約の解釈は日本法に準拠します。本サービスに関する紛争については、東京地方裁判所を第一審の専属的合意管轄裁判所とします。',
              'These Terms are governed by the laws of Japan. The Tokyo District Court shall have exclusive jurisdiction over any disputes.'
            )}
          </p>
        </Body>
      </Section>

      <LastUpdated>{t('最終更新日: 2026年5月22日', 'Last updated: May 22, 2026')}</LastUpdated>
    </Container>
  );
}

export default TermsPage;
